import React, { useState } from 'react';

import { Search } from '@videoblocks/react-icons';

import TopNavSearchBar from './TopNavSearchBar';
import DesktopNavActions from './desktop/DesktopNavActions';
import DesktopNavItem from './desktop/DesktopNavItem';
import MobileNav from './mobile/MobileNav';
import TabletNavActions from './tablet/TabletNavActions';
import type {
  DesktopNavConfig,
  MobileNavConfig,
  TabletNavConfig,
} from './types';

interface TopNavigationBarProps {
  desktop: DesktopNavConfig;
  tablet: TabletNavConfig;
  mobile: MobileNavConfig;
  logoUrl: string;
  showSearch?: boolean;
}

export default function TopNavigationBar({
  desktop,
  tablet,
  mobile,
  logoUrl,
  showSearch = false,
}: TopNavigationBarProps): JSX.Element {
  const [isSearchExpanded, setIsSearchExpanded] = useState(showSearch);

  return (
    <nav
      aria-label="Main navigation"
      className="sticky top-0 z-50 w-full border-b border-gray-200 bg-white"
    >
      <div className="flex items-center justify-between p-4 topnav-xl:px-6 topnav-xl:py-4">
        <div className="flex items-center gap-5">
          <a
            href="/"
            className="flex-shrink-0 no-underline"
            aria-label="Storyblocks home"
          >
            {/* Wordmark — shown 0-869 AND 1258+ (mobile, full desktop) */}
            <img
              src={logoUrl}
              alt="Storyblocks"
              width={151}
              height={32}
              className="block topnav-sm:hidden topnav-xl:block"
            />
            {/* Small-circle logo — shown 870-1257 (tablet + small desktop).
                CSS-cropped from the wordmark so no new asset is needed. */}
            <span className="hidden topnav-sm:block topnav-xl:hidden h-8 w-8 overflow-hidden">
              <img
                src={logoUrl}
                alt=""
                aria-hidden="true"
                className="block h-8 w-auto max-w-none"
              />
            </span>
          </a>
          {/* Tablet inline items (870 to 1025) */}
          <div className="hidden items-center topnav-sm:flex topnav-md:hidden">
            {tablet.navItems.map((item) => (
              <DesktopNavItem key={item.id} config={item} />
            ))}
          </div>
          {/* Desktop inline items (1026+) — Business/Resources hidden 1026-1257 */}
          <div className="hidden items-center topnav-md:flex">
            {desktop.navItems.map((item) => {
              const hiddenAtSmallDesktop =
                item.id === 'businesses' || item.id === 'resources';
              return (
                <span
                  key={item.id}
                  className={
                    hiddenAtSmallDesktop
                      ? 'hidden topnav-xl:inline-flex'
                      : 'inline-flex'
                  }
                >
                  <DesktopNavItem config={item} />
                </span>
              );
            })}
            {!isSearchExpanded && (
              <button
                type="button"
                onClick={() => setIsSearchExpanded(true)}
                aria-label="Open search"
                className="ml-2 p-2 text-gray-600 hover:text-gray-900"
              >
                <Search className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>

        <div className="flex items-center gap-4">
          <DesktopNavActions items={desktop.actions} />
          <TabletNavActions config={tablet} logoUrl={logoUrl} />
          <MobileNav config={mobile} logoUrl={logoUrl} />
        </div>
      </div>

      {/* Below topnav-md (0-1025): always rendered, hidden at topnav-md+ via CSS — no JS, no flash */}
      <div className="flex justify-center pb-2 px-4 topnav-md:hidden">
        <TopNavSearchBar onCollapse={() => {}} showXButton={false} />
      </div>

      {/* topnav-md and up (1026+): render only when expanded; SSR matches because
          isSearchExpanded is initialized from the server-passed showSearch */}
      {isSearchExpanded && (
        <div className="hidden justify-center pb-2 px-6 topnav-md:flex">
          <TopNavSearchBar
            onCollapse={() => setIsSearchExpanded(false)}
            showXButton
          />
        </div>
      )}
    </nav>
  );
}
